import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * 강스터디 인증 — ecotwin Supabase 프로젝트를 web/ 과 공유한다.
 * auth.users 는 프로젝트당 하나라 강스터디 로그인 = echotwin 로그인이다.
 *
 * admin 여부는 **여기서 판정하지 않는다.** 판정 정본은 env ADMIN_USER_IDS 이고
 * (web/lib/admin-ids.ts 와 같은 목록), Edge Function kangstudy-admin 의
 * action:"check" 에 물어본다. 클라이언트에 admin 목록을 복제하면 정본이
 * 둘로 갈라진다.
 */
interface AuthValue {
  user: User | null;
  session: Session | null;
  /** 최초 세션 복원이 끝났는지. true 인 동안은 로그인 여부를 판단하면 안 된다. */
  loading: boolean;
  isAdmin: boolean;
  /** admin 조회가 끝났는지. false 면 아직 모른다(권한 없음과 구분). */
  adminChecked: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (
    email: string,
    password: string,
    meta?: Record<string, unknown>,
  ) => Promise<{ error?: string; needsEmailConfirm?: boolean }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Supabase 영문 오류를 화면에 그대로 뱉지 않는다. */
function toKoreanAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("invalid login credentials")) return "이메일 또는 비밀번호가 올바르지 않습니다.";
  if (m.includes("email not confirmed")) return "이메일 인증이 아직 완료되지 않았습니다. 받은 메일함을 확인해 주세요.";
  if (m.includes("user already registered") || m.includes("already been registered"))
    return "이미 가입된 이메일입니다. 로그인해 주세요.";
  if (m.includes("password should be at least")) return "비밀번호는 6자 이상이어야 합니다.";
  if (m.includes("rate limit") || m.includes("too many")) return "요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.";
  return message;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // 세션이 바뀔 때마다 admin 여부를 정본(Edge Function)에 물어본다.
  useEffect(() => {
    let cancelled = false;

    if (!session) {
      setIsAdmin(false);
      setAdminChecked(true);
      return;
    }

    setAdminChecked(false);
    supabase.functions
      .invoke("kangstudy-admin", { body: { action: "check" } })
      .then(({ data, error }) => {
        if (cancelled) return;
        // 조회 실패는 "권한 없음"으로 닫는다(fail-closed).
        setIsAdmin(!error && Boolean((data as { isAdmin?: boolean } | null)?.isAdmin));
        setAdminChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error ? { error: toKoreanAuthError(error.message) } : {};
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, meta?: Record<string, unknown>) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: meta },
      });
      if (error) return { error: toKoreanAuthError(error.message) };
      // 이메일 확인이 켜져 있으면 session 이 null 로 온다.
      return { needsEmailConfirm: !data.session };
    },
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      isAdmin,
      adminChecked,
      signIn,
      signUp,
      signOut,
    }),
    [session, loading, isAdmin, adminChecked, signIn, signUp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 는 AuthProvider 안에서만 쓸 수 있습니다.");
  return ctx;
}
